import { GetStaticPaths, GetStaticProps } from 'next';
import { isEmpty, last, pick } from 'lodash';
import type { ReactElement } from 'react';

import { Article, PagePaths } from '@/lib/types';
import { CATEGORY_SLUGS, PAGE_REVALIDATE_INTERVAL, SLUGS, STATIC_PATH_LIMIT } from '@/lib/constants';
import { ContentBlocks } from '@/components/content-blocks';
import { getAllModels, getAllPathsBySlug, getArticlePageBySlug } from '@/lib/datocms/api';
import { getPageUrlArray } from '@/lib/datocms/utils/get-page-url';
import { Layout } from '@/components/layout';
import { LinkListBlock } from '@/components/link-list-block';
import { PageBanner } from '@/components/page-banner';
import { Tag } from '@/components/tag';

type Params = { category: string; slug: string };
type PageProps = { article: Article; paths: PagePaths };

export const getStaticPaths: GetStaticPaths = async () => {
  const { articles } = await getAllModels();
  return {
    paths: articles
      .slice(0, STATIC_PATH_LIMIT)
      .map((article) => ({ params: { category: article.categoryPage?.slug, slug: article.slug } })),
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<PageProps, Params> = async ({ params }) => {
  const { category, slug } = params as Params;

  // @NOTE: This is an unfortunate work around because the rewrite can't supply more info
  // about the category for these custom routes that use this template. Ideally
  // the rewrite rules would allow you to specify more informmation about the category param
  let categorySlug = null;
  if (category === 'tassies-top-tourism-towns') {
    categorySlug = ['travel-and-experience', category];
  } else if (category.endsWith('-hub')) {
    categorySlug = ['community', 'our-learning-centre', category];
  } else {
    categorySlug = [category];
  }

  const data = await getArticlePageBySlug({ categorySlug, slug });

  if (!data || !data.article) {
    return {
      notFound: true,
    };
  }
  const { article } = data;

  const pageUrlSlugs = getPageUrlArray(article.categoryPage);
  const isMainCategoryPage = Object.values(CATEGORY_SLUGS).includes(article.categoryPage.slug);
  const parentPaths = await getAllPathsBySlug({
    slug: pageUrlSlugs.concat(isMainCategoryPage ? [SLUGS.RELATED_ARTICLES] : []),
  });
  const paths = parentPaths.concat([
    {
      ...pick(article, ['id', 'slug', 'title']),
      path: [last(parentPaths)?.path, article.slug].join('/'),
    },
  ]);

  return {
    props: { article, paths },
    revalidate: PAGE_REVALIDATE_INTERVAL,
  };
};

const ArticlePage = ({ article, paths }: PageProps): ReactElement => {
  const hasRelatedContent = !isEmpty(article.relatedContent);
  const hasRelatedArticles = !isEmpty(article.relatedArticles);
  return (
    <Layout metaTags={article.metaTags} paths={paths}>
      <div className="component content article-page">
        <div className="component-content">
          <PageBanner heroImage={article.heroImage} />
          <article>
            <div className="article-header width-narrow">
              <div className="component-content">
                <Tag tag={article.tag} />
                <h1 className="display-1 text-active-blue mt-2">{article.title}</h1>
                {!isEmpty(article.lead) && <p className="h4 text-gray-800 mt-4">{article.lead}</p>}
              </div>
            </div>
            <ContentBlocks blocks={article.content} context={{ isInline: true }} />
          </article>
          {(hasRelatedContent || hasRelatedArticles) && (
            <aside>
              {hasRelatedContent && <ContentBlocks blocks={article.relatedContent} />}
              {hasRelatedArticles && (
                <LinkListBlock
                  id="unknown"
                  type="link_list_block"
                  title="Related articles"
                  layout="Grid"
                  theme="Light"
                  items={article.relatedArticles}
                />
              )}
            </aside>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ArticlePage;
